import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Banknote, CheckCircle2, Clock, XCircle, FileText, Trash2, Plus } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAdvances() {
  const { token, user } = useAuth();
  const { primaryColor } = useTheme();
  const navigate = useNavigate();
  const [advances, setAdvances] = useState([]);
  const [clients, setClients] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAnalyst, setFilterAnalyst] = useState('all');
  const [filterOffice, setFilterOffice] = useState('all');
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ client_id: '', amount: '', date: new Date().toISOString().slice(0, 10), due_date: '', penalty_rate: '', penalty_period: 'month' });

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    try {
      const [advRes, cliRes, analRes, offRes] = await Promise.all([
        axios.get(`${API}/advances`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/analysts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setAdvances(advRes.data || []);
      setClients(cliRes.data || []);
      setAnalysts(analRes.data || []);
      setOffices(Array.isArray(offRes.data) ? offRes.data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getClientName = (clientId) => {
    const c = clients.find(cl => cl.user_id === clientId || cl.id === clientId);
    if (c) {
      const u = c.user || c;
      return `${u.first_name || ''} ${u.last_name || ''}`.trim() || c.email || clientId;
    }
    return clientId?.slice(0, 8) || '?';
  };

  const handleCreate = async () => {
    if (!createForm.client_id || !createForm.amount) { toast.error('Client et montant requis'); return; }
    try {
      await axios.post(`${API}/advances`, { ...createForm, amount: parseFloat(createForm.amount), penalty_rate: parseFloat(createForm.penalty_rate) || 0 }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Avance de fonds créée');
      setCreateDialog(false);
      setCreateForm({ client_id: '', amount: '', date: new Date().toISOString().slice(0, 10), due_date: '', penalty_rate: '', penalty_period: 'month' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };


  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/advances/${id}`, { action: 'approve' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Avance validée et créditée');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette avance ?')) return;
    try {
      await axios.delete(`${API}/advances/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Supprimée');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const statusConfig = {
    pending_signature: { label: 'En attente signature', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
    signed: { label: 'Signée — À valider', color: 'bg-blue-500/20 text-blue-400', icon: FileText },
    active: { label: 'Active', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle2 },
    repaid: { label: 'Remboursée', color: 'bg-gray-500/20 text-gray-400', icon: CheckCircle2 },
    cancelled: { label: 'Annulée', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  };

  const getClientAnalyst = (clientId) => {
    const c = clients.find(cl => cl.user_id === clientId || cl.id === clientId);
    return c?.assigned_analyst_id || '';
  };

  const getAnalystOffice = (analystId) => {
    const a = analysts.find(x => x.id === analystId);
    return a?.office_id || '';
  };

  const filteredAdvances = advances.filter(adv => {
    if (filterAnalyst !== 'all') {
      const analystId = getClientAnalyst(adv.client_id);
      if (analystId !== filterAnalyst) return false;
    }
    if (filterOffice !== 'all') {
      const analystId = getClientAnalyst(adv.client_id);
      const officeId = getAnalystOffice(analystId);
      if (officeId !== filterOffice) return false;
    }
    return true;
  });

  const signed = filteredAdvances.filter(a => a.status === 'signed');
  const pending = filteredAdvances.filter(a => a.status === 'pending_signature');
  const active = filteredAdvances.filter(a => a.status === 'active');
  const other = filteredAdvances.filter(a => ['repaid', 'cancelled'].includes(a.status));

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="spinner" /></div></DashboardLayout>;

  const AdvanceRow = ({ adv }) => {
    const sc = statusConfig[adv.status] || {};
    const Icon = sc.icon || Clock;
    const pct = adv.amount > 0 ? ((adv.amount - (adv.remaining || adv.amount)) / adv.amount * 100) : 0;
    const periodLabels = { day: 'jour', week: 'semaine', month: 'mois' };
    return (
      <Card className="bg-card/80 border-border/30" data-testid={`advance-${adv.id}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <p className="text-lg font-bold">{adv.amount?.toLocaleString('fr-FR')} €</p>
                <Badge className={`${sc.color} text-xs`}><Icon className="w-3 h-3 mr-1" />{sc.label}</Badge>
              </div>
              <p className="text-sm">
                <button onClick={() => navigate(`/admin/clients/${adv.client_id}`)} className="hover:underline font-medium">{getClientName(adv.client_id)}</button>
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
                <span>Ref: AVF-{adv.id?.slice(0, 8).toUpperCase()}</span>
                <span>Créée le {adv.date}</span>
                {adv.due_date && <span>Échéance: {adv.due_date}</span>}
                {adv.penalty_rate > 0 && <span className="text-red-400">Pénalité: {adv.penalty_rate}% / {periodLabels[adv.penalty_period] || 'mois'}</span>}
              </div>
              {adv.status === 'active' && (
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span>Remboursé: {((adv.amount || 0) - (adv.remaining || 0)).toLocaleString('fr-FR')} €</span>
                    <span>Reste: {adv.remaining?.toLocaleString('fr-FR')} €</span>
                  </div>
                  <div className="w-full h-1.5 bg-secondary rounded-full"><div className="h-1.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {adv.status === 'signed' && (
                <Button size="sm" onClick={() => handleApprove(adv.id)} style={{ backgroundColor: primaryColor }}>Valider & créditer</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => window.open(`${API}/advances/${adv.id}/contract?token=${token}`, '_blank')}>PDF</Button>
              {!['repaid'].includes(adv.status) && (
                <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(adv.id)}><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-advances-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Avances de Fonds</h1>
            <p className="text-sm text-muted-foreground">{filteredAdvances.length} avance(s) · {signed.length} à valider</p>
          </div>
          <Button onClick={() => { setCreateForm({ client_id: '', amount: '', date: new Date().toISOString().slice(0, 10), due_date: '', penalty_rate: '', penalty_period: 'month' }); setCreateDialog(true); }} style={{ backgroundColor: primaryColor }}>
            <Plus className="w-4 h-4 mr-2" />Nouvelle avance
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          {user?.role !== 'bureau' && user?.role !== 'analyst' && offices.length > 0 && (
            <Select value={filterOffice} onValueChange={setFilterOffice}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Bureau" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les bureaux</SelectItem>
                {offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {user?.role !== 'analyst' && analysts.length > 0 && (
            <Select value={filterAnalyst} onValueChange={setFilterAnalyst}>
              <SelectTrigger className="w-48"><SelectValue placeholder="Analyste" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les analystes</SelectItem>
                {analysts.map(a => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Signed - needs approval */}
        {signed.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-blue-400 flex items-center gap-2">
              <FileText className="w-4 h-4" /> À valider ({signed.length})
            </h2>
            {signed.map(adv => <AdvanceRow key={adv.id} adv={adv} />)}
          </div>
        )}

        {/* Pending signature */}
        {pending.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-yellow-400 flex items-center gap-2">
              <Clock className="w-4 h-4" /> En attente de signature ({pending.length})
            </h2>
            {pending.map(adv => <AdvanceRow key={adv.id} adv={adv} />)}
          </div>
        )}

        {/* Active */}
        {active.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-400 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Actives ({active.length})
            </h2>
            {active.map(adv => <AdvanceRow key={adv.id} adv={adv} />)}
          </div>
        )}

        {/* Other */}
        {other.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Historique ({other.length})</h2>
            {other.map(adv => <AdvanceRow key={adv.id} adv={adv} />)}
          </div>
        )}

        {filteredAdvances.length === 0 && (
          <Card><CardContent className="py-12 text-center text-muted-foreground">Aucune avance de fonds</CardContent></Card>
        )}

        {/* Create Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nouvelle avance de fonds</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Client *</Label>
                <Select value={createForm.client_id} onValueChange={v => setCreateForm(p => ({ ...p, client_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {clients.map(c => {
                      const u = c.user || c;
                      return <SelectItem key={c.user_id || c.id} value={c.user_id || c.id}>{u.first_name} {u.last_name}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Montant (€) *</Label><Input type="number" step="0.01" autoComplete="off" value={createForm.amount} onChange={e => setCreateForm(p => ({ ...p, amount: e.target.value }))} placeholder="5000" /></div>
              <div><Label>Date de l'avance</Label><Input type="date" autoComplete="off" value={createForm.date} onChange={e => setCreateForm(p => ({ ...p, date: e.target.value }))} /></div>
              <div><Label>Date limite de remboursement</Label><Input type="date" autoComplete="off" value={createForm.due_date} onChange={e => setCreateForm(p => ({ ...p, due_date: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Pénalités (%)</Label><Input type="number" step="0.1" autoComplete="off" value={createForm.penalty_rate} onChange={e => setCreateForm(p => ({ ...p, penalty_rate: e.target.value }))} placeholder="5" /></div>
                <div><Label>Par</Label>
                  <Select value={createForm.penalty_period} onValueChange={v => setCreateForm(p => ({ ...p, penalty_period: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="day">Jour</SelectItem><SelectItem value="week">Semaine</SelectItem><SelectItem value="month">Mois</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialog(false)}>Annuler</Button>
              <Button onClick={handleCreate} style={{ backgroundColor: primaryColor }}>Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
